import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Col, Input, Label, Row } from 'reactstrap';

import styles from './style.module.scss';

import { GetPlanId, LineType } from '../../../helpers/Helpers';

import SectionLoader from '../../../components/Common/loader/SectionLoader';
import NoDataFound from '../../../components/Common/no-data-found/no-data-found';
import LineChart from '../../../components/Common/Charts/LineChart';

import { ISalesGrowthAnalysisChart } from '../../../interfaces/charts.interface';
import { IMeasureVisibilityPayload } from '../../../interfaces/common.interface';
import { GetSalesGrowthAnalysisChartData } from '../../../api-services/rolling-plan-charts.api';
import { UpdateMeasureVisibility } from '../../../api-services/common.api';

interface OwnProps {
  isCallRequired: boolean;
  onLoadEnd: () => void;
}

interface StateProps {
  history: any;
}

interface DispatchProps {
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  isLoading: boolean;
  originalDataSource: ISalesGrowthAnalysisChart[];
  dataSource: ISalesGrowthAnalysisChart[];
  periodList: string[];
  selectedPeriod: string;
  isModalOpened: boolean;
}

class SalesGrowthAnalysis extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      originalDataSource: [],
      dataSource: [],
      periodList: ['QUARTER', 'WEEK'],
      selectedPeriod: 'WEEK',
      isModalOpened: false,
    };
  }

  componentDidMount(): void {
    this.getSalesGrowthAnalysisChartData();
  }

  componentDidUpdate(prevProps, prevState): void {
    if (!prevProps.isCallRequired && this.props.isCallRequired) {
      this.getSalesGrowthAnalysisChartData();
    }
  }

  getSalesGrowthAnalysisChartData(): void {
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname)
    };

    this.setState({ isLoading: true });

    GetSalesGrowthAnalysisChartData(this.state.selectedPeriod.toLocaleLowerCase(), params)
      .then((res: ISalesGrowthAnalysisChart[]) => {
        this.setState({
          originalDataSource: !!res ? res : []
        }, () => {
          this.dataInit();
          this.props.onLoadEnd();
        });
      });
  }

  dataInit(): void {
    const data: ISalesGrowthAnalysisChart[] = [...this.state.originalDataSource];
    const dataSource: ISalesGrowthAnalysisChart[] = data.map((group) => {
      return {
        ...group,
        checked: this.state.originalDataSource.length === 1 ? true : (group.name === 'REVENUE'),
        expanded: true,
        disabled: group.name === 'REVENUE',
        children: group.children.map((subgroup) => {
          return {
            ...subgroup,
            checked: subgroup.checked,
            prevChecked: subgroup.checked,
          };
        })
      };
    });

    this.setState({
      originalDataSource: dataSource,
      isLoading: false
    }, () => {
      this.updateDataSource();
    });
  }

  onUpdateGroup(groupIdx: number): void {
    this.state.originalDataSource[groupIdx].checked = !this.state.originalDataSource[groupIdx].checked;

    const checkCount = this.state.originalDataSource.reduce((acc, group) => {
      acc += group.checked ? 1 : 0;
      return acc;
    }, 0);

    if (checkCount === 1) {
      const checkedIndex = this.state.originalDataSource.findIndex((group) => group.checked);
      this.state.originalDataSource[checkedIndex].disabled = true;
    } else {
      const disabledIndex = this.state.originalDataSource.findIndex((group) => group.disabled);
      if (disabledIndex > -1) {
        this.state.originalDataSource[disabledIndex].disabled = false;
      }
    }

    this.updateDataSource();
  }

  onUpdateSubgroup(groupIdx: number, subGroupIdx: number): void {
    this.state.originalDataSource[groupIdx].children[subGroupIdx].checked =
      !this.state.originalDataSource[groupIdx].children[subGroupIdx].checked;

    this.updateDataSource();
  }

  updateDataSource(): void {
    const filteredDataSource = this.state.originalDataSource
      .filter((group) => group.checked && group.children
        .some((subgroup) => subgroup.checked))
      .map((group) => Object.assign({}, group, {
        children: group.children.filter(subgroup => subgroup.checked)
      }));

    this.setState({ dataSource: filteredDataSource });
  }

  toggleExpandLegend(groupIdx: number): void {
    this.state.originalDataSource[groupIdx].expanded = !this.state.originalDataSource[groupIdx].expanded;
    this.setState({ originalDataSource: this.state.originalDataSource });
  }

  togglePeriod(period: string): void {
    this.setState({
      selectedPeriod: period
    }, () => {
      this.getSalesGrowthAnalysisChartData();
    });
  }

  updateMeasureVisibility(): void {
    const payload: IMeasureVisibilityPayload = {
      visibility: this.state.originalDataSource
        .flatMap(item => item.children
          .map(measure => ({
              name: measure.name,
              isVisible: measure.checked,
            })
          )
        )
    };
    const params = {
      userId: sessionStorage.getItem('userName'),
      planId: GetPlanId(this.props.history.location.pathname),
      chart: 'salesGrowthAnalysis',
    };

    UpdateMeasureVisibility(payload, params)
      .then((res) => {
        if (!!res) {
          this.setState({
            isModalOpened: true
          }, () => {
            this.getSalesGrowthAnalysisChartData();
          });
        }
      });
  }

  checkVisibilitySaveButton(): boolean {
    return this.state.originalDataSource
      .every((item) => item.children
        .every((measure) => measure.checked === measure.prevChecked));
  }

  render(): React.ReactNode {
    if (this.state.isLoading) {
      return <SectionLoader/>;
    }

    return (
      <React.Fragment>
        <Row style={{ marginTop: '18px' }}>
          <Col md={9} className="text-center">

            <Row className="align-items-center">
              <Col md={{ offset: 3, size: 3 }}>

                <div className="d-flex">
                  {
                    this.state.periodList.map((period: string, idx: number) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center"
                      >
                        <Input
                          id={period}
                          type="radio"
                          checked={period === this.state.selectedPeriod}
                          onChange={() => this.togglePeriod(period)}
                          className="me-1"
                        />
                        <Label
                          for={period}
                          className={styles['measure-group']}
                        >
                          {period}
                        </Label>
                      </div>
                    ))
                  }
                </div>

              </Col>

              <Col md={4}>
                <div className="d-flex">
                  {
                    this.state.originalDataSource.map((group, idx: number) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center"
                      >
                        <Input
                          id={group.name}
                          type="checkbox"
                          checked={group.checked}
                          disabled={group.disabled}
                          onChange={() => this.onUpdateGroup(idx)}
                          className="me-1"
                        />
                        <Label
                          for={group.name}
                          className={styles['measure-group']}
                        >
                          {group.name}
                        </Label>
                      </div>
                    ))
                  }
                </div>
              </Col>

              <Col md={1}>
                <Button
                  color={'primary'}
                  className='btn btn-primary btn-sm'
                  disabled={this.checkVisibilitySaveButton()}
                  onClick={() => this.updateMeasureVisibility()}
                >
                  Save
                </Button>
              </Col>
            </Row>

            {
              !this.state.isLoading && this.state.dataSource.length === 0
                ? <NoDataFound/>
                : <LineChart
                  dataSource={this.state.dataSource}
                  component={'SalesAnalysis'}
                  period={this.state.selectedPeriod}
                />
            }

          </Col>

          <Col md={3}>

            <div className={styles['legend-section']}>
              {
                this.state.originalDataSource.map((group, groupIdx: number) => (
                  <React.Fragment key={groupIdx}>
                    {
                      group.checked &&
                      <div
                        className={styles['legend-container']}
                        onClick={() => this.toggleExpandLegend(groupIdx)}
                      >
                        <span className={styles['legend-header']}>
                          {group.name} Measure
                        </span>
                        <div className={`${styles['line']} ${styles[LineType(group.name)]}`}/>
                        <i
                          className={`ri-arrow-${group.expanded ? 'up' : 'down'}-s-line`}
                          style={{ fontSize: '22px' }}
                        />
                      </div>
                    }

                    {
                      (group.checked && group.expanded && group.children.length !== 0) &&
                      group.children.map((subGroup, subGroupIdx: number) => (
                        <React.Fragment key={subGroupIdx}>
                          <div className="d-flex align-items-center my-2">
                            <Input
                              id={subGroup.name}
                              type="checkbox"
                              checked={subGroup.checked}
                              onChange={() => this.onUpdateSubgroup(groupIdx, subGroupIdx)}
                            />

                            <div
                              className={styles['legend-color-box']}
                              style={{ backgroundColor: subGroup.color }}
                            />

                            <Label
                              for={subGroup.name}
                              className={styles['line-chart-legend-label']}
                            >
                              {subGroup.name}
                            </Label>
                          </div>
                        </React.Fragment>
                      ))
                    }
                  </React.Fragment>
                ))
              }
            </div>

          </Col>
        </Row>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any): StateProps => {
  return state;
};

export default connect<StateProps, DispatchProps, OwnProps>
(mapStateToProps, {})(withRouter(SalesGrowthAnalysis));
